import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  LdsButton,
  LdsSearch,
  LdsSelect,
  useToastContext,
} from "@elilillyco/ux-lds-react";

import ApprovedSpecialtiesTable from "../components/approvedSpecialties/ApprovedSpecialtiesTable";
import CLoader from "../components/shared/CLoader";
import infoToastConfig from "../components/shared/Toast";

import {
  FETCH_FILTER_LIST,
  FETCH_APPROVED_SPECIALTIES,
} from "../store/approvedSpecialties";

import exportExcelSheet from "../utils/overview/exportExcelSheet";

import "../assets/scss/apprSpecialties/approvedSpecialties.scss";

const ApprovedSpec = () => {
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedIndication, setSelectedIndication] = useState("");
  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [searchText, setSearchText] = useState("");
  const [firstRender, setFirstRender] = useState(true);
  const [visibleSpecialties, setVisibleSpecialties] = useState([]);

  const { addToast } = useToastContext();

  const getTableData = (searchValue) => {
    return {
      brand: selectedBrand === null ? "" : selectedBrand,
      indication: selectedIndication === null ? "" : selectedIndication,
      quarter: selectedQuarter === null ? "" : selectedQuarter,
      department: selectedDepartment === null ? "" : selectedDepartment,
      searchKeyword: searchValue === undefined ? searchText : searchValue,
    };
  };

  const dispatch = useDispatch();
  const {
    filterBrand,
    filterIndication,
    filterQuarter,
    filterDepartment,
    loading,
    approvedSpecialties,
  } = useSelector(({ approvedSpecialties }) => approvedSpecialties);

  const getOptions = (options) => {
    const newOptions = options.map((option) => ({
      value: option,
      label: option,
    }));
    const emptyOption = { label: "Select", value: "" };
    newOptions.unshift(emptyOption);
    return newOptions;
  };

  useEffect(() => {
    dispatch(FETCH_FILTER_LIST());
    dispatch(FETCH_APPROVED_SPECIALTIES(getTableData()));
    setFirstRender(false);
  }, []);

  useEffect(() => {
    if (!firstRender) {
      dispatch(FETCH_APPROVED_SPECIALTIES(getTableData()));
    }
  }, [selectedBrand, selectedIndication, selectedQuarter, selectedDepartment]);

  useEffect(() => {
    const tempSpecialties = JSON.parse(JSON.stringify(approvedSpecialties));
    if (tempSpecialties.length) {
      tempSpecialties.forEach((specialty) => {
        if (specialty.department_field === "RETAIL") {
          specialty.department_field = "COMMERCIAL";
        }
      });
    }
    setVisibleSpecialties(tempSpecialties);
  }, [approvedSpecialties]);

  const submitSearch = () => {
    dispatch(FETCH_APPROVED_SPECIALTIES(getTableData()));
  };

  const clearAllFilters = () => {
    setSelectedBrand("");
    setSelectedIndication("");
    setSelectedQuarter("");
    setSelectedDepartment("");
    setSearchText("");

    // fetching data here if only search field was set else fetching it in useEffect
    if (
      selectedBrand === "" &&
      selectedIndication === "" &&
      selectedQuarter === "" &&
      selectedDepartment === "" &&
      searchText !== ""
    ) {
      dispatch(FETCH_APPROVED_SPECIALTIES(getTableData("")));
    }
  };

  /**
   * Converts the date object to string with format - January 01, 2024
   *
   * @param {Object} dateObj
   * @returns
   */
  const convertDateFormat = (dateObj) => {
    return dateObj ? moment(dateObj).format("MMMM DD, YYYY") : "";
  };

  /**
   * Exports the specialties data to CSV file
   */
  const exportData = () => {
    const dataToExport = [];

    // checking specialties is an array
    if (!Array.isArray(visibleSpecialties)) {
      addToast(
        infoToastConfig("Failed to export the specialties data.", "error")
      );
      return;
    }

    // checking specialties data is present
    if (!visibleSpecialties.length) {
      addToast(infoToastConfig("No specialty data to export.", "warning"));
      return;
    }

    visibleSpecialties.forEach((specialty) => {
      dataToExport.push({
        Title: specialty.list_title || "",
        Product: specialty.brand || "",
        Indication: specialty.indication || "",
        Quarter: specialty.list_quarter || "",
        Department: specialty.department_field || "",
        "Approved Date": convertDateFormat(specialty.updated_date),
      });
    });

    exportExcelSheet(dataToExport, "Approved Specialties");
  };

  return (
    <>
      <section className="section">
        <div className="approved-page">
          <div className="mt-3">
            <div className="row ">
              <div className="col-9">
                <h2 className="pl-4">Approved Specialties</h2>
              </div>
              <div className="col-3 export-button-col">
                <LdsButton
                  classes={`button-icon-left mr-3 ${
                    loading ? "outline secondary" : "blue-btn"
                  }`}
                  icon="DownloadSimple"
                  iconPosition="before"
                  onClick={exportData}
                  disabled={loading ? "disabled" : ""}
                >
                  Export
                </LdsButton>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-6">
              <LdsSearch
                id={"searchSpecialty"}
                onChange={setSearchText}
                value={searchText}
                onSubmit={submitSearch}
              />
            </div>
            <div className="col-6">
              <LdsButton onClick={clearAllFilters}>Clear All</LdsButton>
            </div>
          </div>
          {filterBrand.length > 0 &&
            filterDepartment.length > 0 &&
            filterQuarter.length > 0 &&
            filterIndication.length > 0 && (
              <div className="row filter">
                <div className="col-2">
                  <LdsSelect
                    id="select-product"
                    name="select-product"
                    label="Product"
                    value={selectedBrand}
                    options={getOptions(filterBrand)}
                    onChange={(option) => {
                      setSelectedBrand(option.value);
                    }}
                  ></LdsSelect>
                </div>
                <div className="col-4">
                  <LdsSelect
                    id="select-indication"
                    name="select-indication"
                    label="Indication"
                    value={selectedIndication}
                    options={getOptions(filterIndication)}
                    onChange={(option) => setSelectedIndication(option.value)}
                  ></LdsSelect>
                </div>
                <div className="col-2">
                  <LdsSelect
                    id="select-quarter"
                    name="select-quarter"
                    label="Quarter"
                    value={selectedQuarter}
                    options={getOptions(filterQuarter)}
                    onChange={(option) => setSelectedQuarter(option.value)}
                  ></LdsSelect>
                </div>
                <div className="col-2">
                  <LdsSelect
                    id="select-department"
                    name="select-department"
                    label="Department"
                    value={selectedDepartment}
                    options={getOptions(filterDepartment)}
                    onChange={(option) => setSelectedDepartment(option.value)}
                  ></LdsSelect>
                </div>
              </div>
            )}
          {loading ? (
            <CLoader size={90} />
          ) : (
            <div className="apprSpecTable">
              <ApprovedSpecialtiesTable
                visibleSpecialties={visibleSpecialties}
                setVisibleSpecialties={setVisibleSpecialties}
                convertDateFormat={convertDateFormat}
              />
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default ApprovedSpec;
